import { CommonModule, DOCUMENT } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { finalize } from 'rxjs';

import { EDITABLE_DOCUMENT_STATUSES, type DocumentStatus, type DocumentSummary } from '../../models/document.models';
import { DocumentApiService } from '../../services/document-api.service';
import { GlobalFeedbackService } from '../../services/global-feedback.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

interface StatusFilter {
  key: 'all' | DocumentStatus;
  label: string;
}

type DocumentActionId =
  | 'view'
  | 'download_document'
  | 'download_attachment'
  | 'delete'
  | 'delete_forever'
  | 'edit'
  | 'restore'
  | 'revoke'
  | 'send_reminder';

interface DocumentAction {
  id: DocumentActionId;
  label: string;
  available: boolean;
  destructive?: boolean;
}

interface DocumentsListQueryState {
  status?: DocumentStatus;
  search?: string;
  page?: number;
  limit?: number;
}

type DocumentsListMode = 'active' | 'trash';
type FixedDocumentsStatus = DocumentStatus | null;

@Component({
  selector: 'app-documents-list',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './documents-list.component.html',
  styleUrl: './documents-list.component.scss',
})
export class DocumentsListComponent implements OnInit {
  documents: DocumentSummary[] = [];
  listMode: DocumentsListMode = 'active';
  fixedStatus: FixedDocumentsStatus = null;

  searchQuery = '';
  appliedSearch = '';
  successMessage = '';

  page = 1;
  limit = 10;
  total = 0;
  totalPages = 1;
  availableLimits = [10, 25, 50, 100];
  goToPageInput = '1';
  statusCounts: Record<string, number> = {};

  loading = false;
  errorMessage = '';
  activeStatus: 'all' | DocumentStatus = 'all';
  hoveredRowId: string | null = null;
  focusedRowId: string | null = null;
  openMenuRowId: string | null = null;
  deleteConfirmDocument: DocumentSummary | null = null;
  busyActionsByRowId: Record<string, DocumentActionId | null> = {};

  readonly filters: StatusFilter[] = [
    { key: 'all', label: 'All' },
    { key: 'waiting_for_others', label: 'Waiting' },
    { key: 'completed', label: 'Completed' },
    { key: 'declined', label: 'Declined' },
    { key: 'scheduled', label: 'Scheduled' },
    { key: 'expired', label: 'Expired' },
    { key: 'revoked', label: 'Revoked' },
    { key: 'needs_attention', label: 'Need Attention' },
    { key: 'draft', label: 'Draft' },
  ];

  private readonly api = inject(DocumentApiService);
  private readonly feedback = inject(GlobalFeedbackService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly documentRef = inject(DOCUMENT);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly statusActions: Partial<Record<DocumentStatus, DocumentAction[]>> = {
    completed: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'download_attachment', label: 'Download attachment', available: false },
      { id: 'delete', label: 'Delete', available: true, destructive: true },
    ],
    waiting_for_others: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'revoke', label: 'Revoke', available: true, destructive: true },
      { id: 'send_reminder', label: 'Send reminder', available: true },
    ],
    draft: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'delete', label: 'Delete', available: true, destructive: true },
    ],
    scheduled: [
      { id: 'download_document', label: 'Download document', available: true },
    ],
    needs_attention: [
      { id: 'download_document', label: 'Download document', available: true },
    ],
    declined: [
      { id: 'download_document', label: 'Download document', available: true },
    ],
    revoked: [
      { id: 'download_document', label: 'Download document', available: true },
    ],
    expired: [
      { id: 'download_document', label: 'Download document', available: true },
      { id: 'delete', label: 'Delete', available: true, destructive: true },
    ],
  };

  ngOnInit(): void {
    this.listMode = this.resolveListMode();
    this.fixedStatus = this.resolveFixedStatus();
    this.restoreStateFromQueryParams();
    this.loadDocuments();
    this.loadDashboardCounts();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.openMenuRowId) {
      return;
    }

    const target = event.target as Node | null;
    if (target && this.hostElement.nativeElement.contains(target)) {
      return;
    }

    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.deleteConfirmDocument) {
      this.closeDeleteConfirm();
      return;
    }

    this.closeMenu();
  }

  applySearch(): void {
    this.page = 1;
    this.appliedSearch = this.searchQuery.trim();
    this.updateListQueryParams();
    this.loadDocuments();
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.appliedSearch = '';
    this.page = 1;
    this.updateListQueryParams();
    this.loadDocuments();
  }

  selectFilter(filter: 'all' | DocumentStatus): void {
    if (this.hasFixedStatus()) {
      return;
    }

    if (this.activeStatus === filter) {
      return;
    }

    this.activeStatus = filter;
    this.page = 1;
    this.updateListQueryParams();
    this.loadDocuments();
  }

  goToPreviousPage(): void {
    if (this.page <= 1 || this.loading) {
      return;
    }

    this.page -= 1;
    this.updateListQueryParams();
    this.loadDocuments();
  }

  goToNextPage(): void {
    if (this.page >= this.totalPages || this.loading) {
      return;
    }

    this.page += 1;
    this.updateListQueryParams();
    this.loadDocuments();
  }

  goToFirstPage(): void {
    this.goToPage(1);
  }

  goToLastPage(): void {
    this.goToPage(this.totalPages);
  }

  goToPage(pageNum: number): void {
    if (pageNum < 1 || pageNum > this.totalPages || pageNum === this.page || this.loading) {
      return;
    }

    this.page = pageNum;
    this.updateListQueryParams();
    this.loadDocuments();
  }

  jumpToPage(): void {
    const raw = String(this.goToPageInput || '').trim();
    const target = parseInt(raw, 10);
    if (!isNaN(target) && target >= 1 && target <= this.totalPages) {
      this.goToPage(target);
      this.goToPageInput = String(target);
    } else {
      this.goToPageInput = String(this.page);
    }
  }

  onLimitChange(newLimit: number): void {
    const parsed = Number(newLimit);
    if (isNaN(parsed) || this.limit === parsed || this.loading) {
      return;
    }

    this.limit = parsed;
    this.page = 1;
    this.updateListQueryParams();
    this.loadDocuments();
  }

  pageNumbers(): number[] {
    const pages: number[] = [];
    const maxVisible = 5;
    let start = Math.max(1, this.page - 2);
    let end = Math.min(this.totalPages, start + maxVisible - 1);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  getFilterCount(key: 'all' | DocumentStatus): number {
    if (key === 'all') {
      return this.statusCounts['all'] ?? this.total;
    }
    if (this.activeStatus === key) {
      return this.total;
    }
    if (this.statusCounts[key] !== undefined) {
      return this.statusCounts[key];
    }
    return this.documents.filter((doc) => doc.status === key).length;
  }

  statusLabel(status: DocumentStatus): string {
    const map: Record<DocumentStatus, string> = {
      waiting_for_others: 'Waiting for Others',
      completed: 'Completed',
      declined: 'Declined',
      scheduled: 'Scheduled',
      expired: 'Expired',
      revoked: 'Revoked',
      needs_attention: 'Needs Attention',
      draft: 'Draft',
    };
    return map[status] ?? status.replaceAll('_', ' ');
  }

  actionsFor(document: DocumentSummary): DocumentAction[] {
    if (this.isTrashMode()) {
      return [
        { id: 'restore', label: 'Restore', available: true },
        { id: 'delete_forever', label: 'Delete forever', available: true, destructive: true },
      ];
    }

    return this.statusActions[document.status] ?? [];
  }

  quickActionFor(document: DocumentSummary): DocumentAction | null {
    const actions = this.actionsFor(document);
    if (this.isTrashMode()) {
      return actions.find((action) => action.id === 'restore') ?? null;
    }

    if (document.status === 'waiting_for_others') {
      return actions.find((action) => action.id === 'revoke') ?? null;
    }

    if (EDITABLE_DOCUMENT_STATUSES.includes(document.status)) {
      return actions.find((action) => action.id === 'edit') ?? null;
    }

    if (document.status === 'completed' || document.status === 'expired') {
      return actions.find((action) => action.id === 'download_document') ?? null;
    }

    return null;
  }

  downloadActionFor(document: DocumentSummary): DocumentAction | null {
    const actions = this.actionsFor(document);
    return actions.find((action) => action.id === 'download_document') ?? null;
  }

  canEdit(document: DocumentSummary): boolean {
    return !this.isTrashMode() && EDITABLE_DOCUMENT_STATUSES.includes(document.status);
  }

  openEdit(document: DocumentSummary): void {
    void this.router.navigate(['/documents', document._id, 'edit']);
  }

  hasActions(document: DocumentSummary): boolean {
    return !this.isTrashMode() || this.actionsFor(document).length > 0;
  }

  showActions(documentId: string): boolean {
    return this.hoveredRowId === documentId || this.focusedRowId === documentId || this.openMenuRowId === documentId;
  }

  formatUpdatedAt(value: string): string {
    return this.dateTimeFormat.formatDateTime(value);
  }

  isMenuOpen(documentId: string): boolean {
    return this.openMenuRowId === documentId;
  }

  isActionBusy(documentId: string, actionId: DocumentActionId): boolean {
    return this.busyActionsByRowId[documentId] === actionId;
  }

  isDeleteConfirmOpen(documentId: string): boolean {
    return this.deleteConfirmDocument?._id === documentId;
  }

  onRowMouseEnter(documentId: string): void {
    if (!this.canInteractWithRows()) {
      return;
    }

    this.hoveredRowId = documentId;
  }

  onRowMouseLeave(documentId: string): void {
    if (this.hoveredRowId === documentId) {
      this.hoveredRowId = null;
    }
  }

  onRowFocusIn(documentId: string): void {
    if (!this.canInteractWithRows()) {
      return;
    }

    this.focusedRowId = documentId;
  }

  onRowFocusOut(event: FocusEvent, documentId: string): void {
    const nextTarget = event.relatedTarget as Node | null;
    const rowElement = this.hostElement.nativeElement.querySelector(`[data-row-id="${documentId}"]`) as HTMLElement | null;
    if (nextTarget && rowElement?.contains(nextTarget)) {
      return;
    }

    if (this.focusedRowId === documentId) {
      this.focusedRowId = null;
    }
  }

  openOverview(document: DocumentSummary): void {
    if (!this.canInteractWithRows()) {
      return;
    }

    void this.router.navigate(['/documents', document._id, 'overview'], {
      queryParams: this.currentListQueryParams(),
    });
  }

  openView(document: DocumentSummary): void {
    void this.router.navigate(['/documents', document._id, 'view'], {
      queryParams: this.currentListQueryParams(),
    });
  }

  onRowKeydown(event: KeyboardEvent, document: DocumentSummary): void {
    if (!this.canInteractWithRows()) {
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.openOverview(document);
  }

  toggleMenu(event: MouseEvent, documentId: string): void {
    event.stopPropagation();
    this.openMenuRowId = this.openMenuRowId === documentId ? null : documentId;
  }

  handleAction(event: Event, document: DocumentSummary, action: DocumentAction): void {
    event.stopPropagation();

    if (!action.available || this.loading || this.isActionBusy(document._id, action.id)) {
      return;
    }

    switch (action.id) {
      case 'download_document':
        this.downloadDocument(document);
        return;
      case 'revoke':
        this.revokeDocument(document);
        return;
      case 'send_reminder':
        this.sendReminder(document);
        return;
      case 'edit':
        void this.router.navigate(['/documents', document._id, 'edit']);
        return;
      case 'restore':
        this.restoreDocument(document);
        return;
      case 'view':
        this.openView(document);
        return;
      case 'delete':
      case 'delete_forever':
        this.openDeleteConfirm(document);
        return;
      default:
        return;
    }
  }

  closeDeleteConfirm(): void {
    this.deleteConfirmDocument = null;
    this.cdr.markForCheck();
  }

  confirmDelete(event: Event): void {
    event.stopPropagation();

    const document = this.deleteConfirmDocument;
    if (!document || this.isActionBusy(document._id, 'delete')) {
      return;
    }

    this.successMessage = '';
    this.setBusyAction(document._id, 'delete');
    const deleteRequest$ = this.isTrashMode()
      ? this.api.deleteDocumentForever(document._id)
      : this.api.deleteDocument(document._id);

    deleteRequest$
      .pipe(
        finalize(() => {
          this.clearBusyAction(document._id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.closeMenu();
          this.deleteConfirmDocument = null;
          this.successMessage = this.isTrashMode()
            ? 'Document deleted forever.'
            : 'Document moved to trash successfully.';
          this.loadDocuments();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: this.isTrashMode()
              ? 'Failed to delete document forever'
              : 'Failed to move document to trash',
          });
          this.cdr.markForCheck();
        },
      });
  }

  formatScheduledAt(value: string): string {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return `Scheduled for ${value}`;
    }

    const localized = this.dateTimeFormat.formatDateTime(parsed, { includeTimeZoneName: true });

    return `Scheduled for ${localized}`;
  }

  isTrashMode(): boolean {
    return this.listMode === 'trash';
  }

  pageTitle(): string {
    if (this.isDraftsMode()) {
      return 'Drafts';
    }

    return this.isTrashMode() ? 'Trash' : 'My Documents';
  }

  pageDescription(): string {
    if (this.isDraftsMode()) {
      return 'Review, update, and finish documents that are still in draft.';
    }

    return this.isTrashMode()
      ? 'Soft-deleted documents are kept here for reference.'
      : 'Manage active documents, track signatures, and take follow-up actions.';
  }

  searchPlaceholder(): string {
    return this.isTrashMode() ? 'Search Deleted Documents...' : 'Search Documents...';
  }

  loadingMessage(): string {
    return this.isTrashMode() ? 'Loading deleted documents…' : 'Loading documents…';
  }

  emptyMessage(): string {
    if (this.isDraftsMode()) {
      return 'No draft documents found.';
    }

    return this.isTrashMode() ? 'Trash is empty.' : 'No documents found.';
  }

  totalLabel(): string {
    if (this.isDraftsMode()) {
      return 'draft documents';
    }

    return this.isTrashMode() ? 'deleted documents' : 'documents';
  }

  rowAriaLabel(): string | null {
    return this.canInteractWithRows() ? 'Open document overview' : null;
  }

  canInteractWithRows(): boolean {
    return !this.isTrashMode();
  }

  visibleFilters(): StatusFilter[] {
    if (this.hasFixedStatus()) {
      return this.filters.filter((filter) => filter.key === this.fixedStatus);
    }

    return this.filters;
  }

  isDraftsMode(): boolean {
    return this.fixedStatus === 'draft' && !this.isTrashMode();
  }

  hasFixedStatus(): boolean {
    return this.fixedStatus !== null;
  }

  deleteModalTitle(): string {
    return this.isTrashMode() ? 'Delete document forever?' : 'Move document to trash?';
  }

  deleteModalMessage(): string {
    return this.isTrashMode()
      ? 'Delete this document forever? This permanently removes the document and its generated files.'
      : 'Move this document to Trash? You can restore it later from the Trash list.';
  }

  deleteModalConfirmLabel(): string {
    return this.isTrashMode() ? 'Delete forever' : 'Move to Trash';
  }

  private closeMenu(): void {
    if (!this.openMenuRowId) {
      return;
    }

    this.openMenuRowId = null;
    this.cdr.markForCheck();
  }

  private openDeleteConfirm(document: DocumentSummary): void {
    this.deleteConfirmDocument = document;
    this.closeMenu();
    this.cdr.markForCheck();
  }

  private downloadDocument(document: DocumentSummary): void {
    if (!document.sourceFile?.fileId && (document.status !== 'completed' || !document.finalSignedFile)) {
      return;
    }

    this.successMessage = '';
    this.setBusyAction(document._id, 'download_document');
    const download$ =
      document.status === 'completed'
        ? this.api.downloadFinalPdf(document._id)
        : this.api.downloadSourcePdf(document.sourceFile.fileId);

    download$
      .pipe(
        finalize(() => {
          this.clearBusyAction(document._id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (blob) => {
          this.closeMenu();
          this.downloadBlob(blob, this.resolveDownloadName(document));
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to download document',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private revokeDocument(document: DocumentSummary): void {
    if (document.status !== 'waiting_for_others') {
      return;
    }

    this.successMessage = '';
    this.setBusyAction(document._id, 'revoke');
    this.api
      .revokeDocument(document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction(document._id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.closeMenu();
          this.successMessage = 'Document revoked successfully.';
          this.loadDocuments();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to revoke document',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private sendReminder(document: DocumentSummary): void {
    if (document.status !== 'waiting_for_others') {
      return;
    }

    this.successMessage = '';
    this.setBusyAction(document._id, 'send_reminder');
    this.api
      .sendReminder(document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction(document._id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.closeMenu();
          const recipientCount = response.queuedRecipientIds.length;
          this.successMessage =
            recipientCount === 1
              ? 'Reminder queued for 1 recipient.'
              : `Reminders queued for ${recipientCount} recipients.`;
          this.loadDocuments();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to send reminder',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private setBusyAction(documentId: string, actionId: DocumentActionId): void {
    this.busyActionsByRowId = {
      ...this.busyActionsByRowId,
      [documentId]: actionId,
    };
  }

  private clearBusyAction(documentId: string): void {
    this.busyActionsByRowId = {
      ...this.busyActionsByRowId,
      [documentId]: null,
    };
  }

  private resolveDownloadName(document: DocumentSummary): string {
    if (document.status !== 'completed') {
      return document.sourceFile.originalName;
    }

    const baseName =
      document.finalSignedFile?.originalName ||
      document.sourceFile.originalName.replace(/\.pdf$/i, '') + '-signed.pdf';
    return baseName.toLowerCase().endsWith('.pdf') ? baseName : `${baseName}.pdf`;
  }

  private downloadBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = this.documentRef.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.style.display = 'none';
    this.documentRef.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  private restoreDocument(document: DocumentSummary): void {
    this.successMessage = '';
    this.setBusyAction(document._id, 'restore');
    this.api
      .restoreDocument(document._id)
      .pipe(
        finalize(() => {
          this.clearBusyAction(document._id);
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.closeMenu();
          this.successMessage = 'Document restored successfully.';
          this.loadDocuments();
        },
        error: (error: unknown) => {
          this.feedback.showNormalizedError(error, {
            fallbackMessage: 'Failed to restore document',
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadDocuments(): void {
    this.loading = true;
    this.openMenuRowId = null;
    const listRequest = this.isTrashMode() ? this.api.listDeletedDocuments.bind(this.api) : this.api.listDocuments.bind(this.api);

    listRequest({
        page: this.page,
        limit: this.limit,
        search: this.appliedSearch,
        status: this.effectiveStatus(),
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (response) => {
          this.feedback.clearError();
          this.errorMessage = '';
          this.documents = response.items;
          this.total = response.total;
          this.totalPages = Math.max(response.totalPages || 1, 1);
          this.page = response.page;

          if (this.activeStatus === 'all') {
            this.statusCounts['all'] = response.total;
          } else {
            this.statusCounts[this.activeStatus] = response.total;
          }
          if ((response as any).statusCounts) {
            this.statusCounts = { ...this.statusCounts, ...(response as any).statusCounts };
          }
          this.goToPageInput = String(this.page);

          this.cdr.markForCheck();
        },
        error: (error: unknown) => {
          const normalized = normalizeApiError(error, {
            fallbackMessage: this.isTrashMode() ? 'Failed to load trash' : 'Failed to load documents',
          });
          this.errorMessage = normalized.message;
          this.successMessage = '';
          this.feedback.showError({
            ...normalized,
            actionLabel: 'Retry',
            action: () => this.loadDocuments(),
          });
          this.cdr.markForCheck();
        },
      });
  }

  private loadDashboardCounts(): void {
    if (this.isTrashMode() || typeof this.api?.getDashboardSummary !== 'function') {
      return;
    }

    this.api.getDashboardSummary().subscribe({
      next: (summary) => {
        if (summary) {
          this.statusCounts['waiting_for_others'] = summary.waitingForOthers?.count ?? 0;
          this.statusCounts['needs_attention'] = summary.needsAttention?.count ?? 0;
          this.statusCounts['completed'] = summary.recentlyCompleted?.count ?? 0;
          if (summary.metrics?.totalDocuments?.total !== undefined) {
            this.statusCounts['all'] = summary.metrics.totalDocuments.total;
          }
          this.cdr.markForCheck();
        }
      },
      error: () => {
        // Non-blocking fallback
      },
    });
  }

  private restoreStateFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const statusParam = params.get('status');
    const searchParam = params.get('search');
    const pageParam = params.get('page');
    const limitParam = params.get('limit');

    if (!this.hasFixedStatus() && statusParam && this.filters.some((filter) => filter.key === statusParam)) {
      this.activeStatus = statusParam as DocumentStatus;
    }

    if (this.hasFixedStatus()) {
      this.activeStatus = this.fixedStatus!;
    }

    if (searchParam) {
      this.searchQuery = searchParam;
      this.appliedSearch = searchParam;
    }

    const parsedPage = this.parsePositiveInteger(pageParam);
    if (parsedPage) {
      this.page = parsedPage;
    }

    const parsedLimit = this.parsePositiveInteger(limitParam);
    if (parsedLimit) {
      this.limit = parsedLimit;
    }
  }

  private updateListQueryParams(): void {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: this.currentListQueryParams(),
      replaceUrl: true,
    });
  }

  private currentListQueryParams(): DocumentsListQueryState {
    return {
      status: this.hasFixedStatus() ? undefined : this.activeStatus === 'all' ? undefined : this.activeStatus,
      search: this.appliedSearch || undefined,
      page: this.page > 1 ? this.page : undefined,
      limit: this.limit !== 10 ? this.limit : undefined,
    };
  }

  private parsePositiveInteger(value: string | null): number | null {
    if (!value) {
      return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private resolveListMode(): DocumentsListMode {
    return this.route.snapshot.data?.['listMode'] === 'trash' ? 'trash' : 'active';
  }

  private resolveFixedStatus(): FixedDocumentsStatus {
    const fixedStatus = this.route.snapshot.data?.['fixedStatus'];
    return typeof fixedStatus === 'string' ? (fixedStatus as DocumentStatus) : null;
  }

  private effectiveStatus(): DocumentStatus | undefined {
    if (this.hasFixedStatus()) {
      return this.fixedStatus!;
    }

    return this.activeStatus === 'all' ? undefined : this.activeStatus;
  }
}
